import { roundRect2 } from '../renderers';
import { lineFont } from '../state/font';
import { drawImage } from '../utils/drawImage';
import { getImage } from '../utils/getImage';

import { BasicObject, getBasicObject } from './basicObject';

const CHECKBOX_MODE = Object.freeze({
  WRONG: Symbol('CHECKBOX_MODE.WRONG'),
  RIGHT: Symbol('CHECKBOX_MODE.RIGHT'),
  SELECTED: Symbol('CHECKBOX_MODE.SELECTED'),
  UNSELECTED: Symbol('CHECKBOX_MODE.UNSELECTED'),
});

const CHECKBOX_SIZE = 25;
const CHECKBOX_CORNER_RADIUS = 6;
const CHECKBOX_BORDER_WIDTH = 2;
const CHECKBOX_MARK_INSET = 4;

type IsTesting = () => boolean;
type OnSelectedChange = (selected: boolean) => void;
type PaintCanvas = (() => void) | undefined;

export interface AttemptGetterCheckbox extends BasicObject {
  setSelectedState(selected: boolean): void;
  setSelected(selected: boolean): void;
  isSelected(): boolean;
  toggleSelected(): void;
  setCorrectlySubmitted(correctness: boolean | undefined): void;
  hasBeenCorrectlySubmitted(): boolean;
  hasBeenIncorrectlySubmitted(): boolean;
  hasNeverBeenSubmitted(): boolean;
  isEnabled(): boolean;
  paintMe(ctx: CanvasRenderingContext2D): void;
}

export interface MultiSelectCheckbox extends AttemptGetterCheckbox {
  letter: string;
  getLetter(): string;
}

interface PaintAttemptGetterCheckboxArgs {
  ctx: CanvasRenderingContext2D;
  checkbox: AttemptGetterCheckbox;
  paintCanvas: PaintCanvas;
  isTesting: IsTesting;
}

interface AttemptGetterCheckboxBuilderArgs {
  isTesting: IsTesting;
  onSelectedChange?: OnSelectedChange;
  paintCanvas?: PaintCanvas;
}

interface MultiSelectCheckboxBuilderArgs
  extends AttemptGetterCheckboxBuilderArgs {
  letter: string;
}

interface MultiChoiceCheckboxBuilderArgs {
  initialSelected?: boolean;
  isTesting: IsTesting;
  paintCanvas?: PaintCanvas;
}

const paintAttemptGetterCheckbox = ({
  ctx,
  checkbox,
  paintCanvas,
  isTesting,
}: PaintAttemptGetterCheckboxArgs): void => {
  let checkboxMode = CHECKBOX_MODE.UNSELECTED;

  if (!isTesting() && checkbox.hasBeenIncorrectlySubmitted()) {
    checkboxMode = CHECKBOX_MODE.WRONG;
  } else if (!isTesting() && checkbox.hasBeenCorrectlySubmitted()) {
    checkboxMode = CHECKBOX_MODE.RIGHT;
  } else if (checkbox.isSelected() && checkbox.hasNeverBeenSubmitted()) {
    checkboxMode = CHECKBOX_MODE.SELECTED;
  }

  const paintBackgroundAndBorder = (
    fillColor?: string,
    borderColor?: string
  ): void => {
    // Paint the checkbox background and border here so the symbols can be layered independently afterward.
    roundRect2(
      ctx,
      0,
      0,
      checkbox.viewportW,
      checkbox.viewportH,
      CHECKBOX_CORNER_RADIUS,
      fillColor,
      borderColor,
      CHECKBOX_BORDER_WIDTH
    );
  };

  // Draw the black checkmark only while the box is selected and it has not been replaced by a right/wrong indicator.
  const paintCheckmark = (): void => {
    ctx.save();
    ctx.fillStyle = 'black';
    ctx.font = 'bold 18px ' + lineFont;
    ctx.textBaseline = 'top';
    ctx.fillText('\u2713', CHECKBOX_MARK_INSET, CHECKBOX_MARK_INSET);
    ctx.restore();
  };

  // In practice mode, wrong submissions get a magenta X layered over the gray fill and border.
  const paintWrongX = (): void => {
    ctx.save();
    ctx.lineWidth = CHECKBOX_BORDER_WIDTH;
    ctx.strokeStyle = 'magenta';
    ctx.beginPath();
    ctx.moveTo(CHECKBOX_BORDER_WIDTH, CHECKBOX_BORDER_WIDTH);
    ctx.lineTo(
      checkbox.viewportW - CHECKBOX_BORDER_WIDTH,
      checkbox.viewportH - CHECKBOX_BORDER_WIDTH
    );
    ctx.moveTo(
      checkbox.viewportW - CHECKBOX_BORDER_WIDTH,
      CHECKBOX_BORDER_WIDTH
    );
    ctx.lineTo(
      CHECKBOX_BORDER_WIDTH,
      checkbox.viewportH - CHECKBOX_BORDER_WIDTH
    );
    ctx.stroke();
    ctx.restore();
  };

  // In practice mode, correct submissions get a thumbs-up symbol layered over white fill and gray border.
  const paintCorrectChoiceThumbsUp = (
    ctx: CanvasRenderingContext2D,
    checkbox: AttemptGetterCheckbox,
    paintCanvas?: PaintCanvas
  ): void => {
    const thumbsUpSize = Math.min(checkbox.viewportW, checkbox.viewportH) * 0.9;

    drawImage(
      ctx,
      getImage('thumbsUp', paintCanvas),
      (checkbox.viewportW - thumbsUpSize) / 2,
      (checkbox.viewportH - thumbsUpSize) / 2,
      thumbsUpSize,
      thumbsUpSize
    );
  };

  switch (checkboxMode) {
    case CHECKBOX_MODE.WRONG:
      // Show the gray wrong-answer feedback state only in practice mode after an incorrect submission.
      paintBackgroundAndBorder('gray', 'gray');
      paintWrongX();
      break;

    case CHECKBOX_MODE.RIGHT:
      // Show the practice-mode correct-answer indicator with a thumbs-up instead of any checkmark.
      paintBackgroundAndBorder('white', 'gray');
      paintCorrectChoiceThumbsUp(ctx, checkbox, paintCanvas);
      break;

    case CHECKBOX_MODE.SELECTED:
      // Show a currently selected but ungraded checkbox with a white fill, cyan border, and black checkmark.
      paintBackgroundAndBorder('white', 'cyan');
      paintCheckmark();
      break;

    case CHECKBOX_MODE.UNSELECTED:
    default:
      // Leave unselected and ungraded checkboxes as a plain white box with a light-gray border.
      paintBackgroundAndBorder('white', 'lightGray');
      break;
  }
};

export const buildAttemptGetterCheckbox = ({
  isTesting,
  onSelectedChange,
  paintCanvas,
}: AttemptGetterCheckboxBuilderArgs): AttemptGetterCheckbox => {
  const checkbox = getBasicObject() as AttemptGetterCheckbox;
  let selected = false;
  let correctness: boolean | undefined;

  checkbox.setAllDim(CHECKBOX_SIZE, CHECKBOX_SIZE);
  checkbox.viewportX = 1;

  checkbox.setSelectedState = (nextSelected: boolean): void => {
    selected = nextSelected;
  };

  checkbox.setSelected = (nextSelected: boolean): void => {
    checkbox.setSelectedState(nextSelected);

    onSelectedChange?.(nextSelected);
  };

  checkbox.isSelected = (): boolean => {
    return selected;
  };

  checkbox.toggleSelected = (): void => {
    checkbox.setSelected(!checkbox.isSelected());
  };

  checkbox.setCorrectlySubmitted = (
    nextCorrectness: boolean | undefined
  ): void => {
    correctness = nextCorrectness;
  };

  checkbox.hasBeenCorrectlySubmitted = (): boolean => {
    return correctness === true;
  };

  checkbox.hasBeenIncorrectlySubmitted = (): boolean => {
    return correctness === false;
  };

  checkbox.hasNeverBeenSubmitted = (): boolean => {
    return correctness === undefined;
  };

  // allow changes to any non-submitted option (this will be ANY option when not testing)
  checkbox.isEnabled = (): boolean => {
    return checkbox.hasNeverBeenSubmitted();
  };

  checkbox.paintMe = (ctx: CanvasRenderingContext2D): void => {
    paintAttemptGetterCheckbox({
      ctx,
      checkbox,
      paintCanvas,
      isTesting,
    });
  };

  return checkbox;
};

export const buildMultiSelectCheckbox = ({
  letter,
  isTesting,
  onSelectedChange,
  paintCanvas,
}: MultiSelectCheckboxBuilderArgs): MultiSelectCheckbox => {
  const checkbox = buildAttemptGetterCheckbox({
    isTesting,
    onSelectedChange,
    paintCanvas,
  }) as MultiSelectCheckbox;

  checkbox.letter = letter;

  checkbox.getLetter = (): string => {
    return checkbox.letter;
  };

  return checkbox;
};

export const buildMultiChoiceCheckbox = ({
  initialSelected,
  isTesting,
  paintCanvas,
}: MultiChoiceCheckboxBuilderArgs): AttemptGetterCheckbox => {
  const checkbox = buildAttemptGetterCheckbox({
    isTesting,
    paintCanvas,
  });

  checkbox.setSelected(initialSelected === true);

  return checkbox;
};
